<script setup lang="ts">
import { computed } from 'vue';
import PlaylistContainerZoneLine from '@/components/DataTable/PlaylistContainerZoneLine.vue';
import type { BulkRunItem } from '@/composables/api/useBulkActionApi';
import { modelIcon } from '@/composables/modelIcons';
import { useOpenRecordInInspector } from '@/composables/useOpenRecordInInspector';

/**
 * Where one bulk action row lives: its custom list and its zone, drawn the way
 * the rest of the app draws them. The list shows its mark and a linked name,
 * and the zone is the usual zone line with its mark and links. It is never a
 * plain "list · zone" string.
 *
 * A row that was not found has neither, so it is named by its id.
 */
const props = defineProps<{ item: BulkRunItem }>();

const listIcon = modelIcon('LocationZoneCustomPlaylist');
const { openRecord } = useOpenRecordInInspector();

const zoneRow = computed(() => (props.item.location_zone ? { location_zone: props.item.location_zone } : null));
</script>

<template>
    <span class="flex min-w-0 flex-col gap-0.5" data-testid="bulk-row-place">
        <span v-if="item.list" class="flex min-w-0 items-center gap-1.5 font-medium">
            <component :is="listIcon" v-if="listIcon" class="size-4 shrink-0" aria-hidden="true" />
            <button
                type="button"
                class="hover:text-primary min-w-0 cursor-pointer truncate text-left hover:underline"
                data-testid="bulk-row-list"
                @click="openRecord('LocationZoneCustomPlaylist', item.list.id)"
            >
                {{ item.list.display_string }}
            </button>
        </span>
        <PlaylistContainerZoneLine v-if="zoneRow" :row="zoneRow" class="min-w-0 text-xs" />
        <span v-if="!item.list && !zoneRow" class="font-medium">Row #{{ item.record_id }}</span>
    </span>
</template>
